/**
 * Widget editor dialog — basic widget metadata plus the raw JSON configuration.
 *
 * Converted off MUI's `Dialog` onto `@coordio/ui`'s (Radix under the hood) because the contents are
 * four fields and a switch: `FormField` supplies the label/description/`aria-describedby` wiring
 * that the MUI version was getting from `TextField`, so nothing is lost and the modal now matches
 * every other modal in the product.
 *
 * Note the positional fields (`positionX`, `positionY`, `width`, `height`, `sortOrder`) are still
 * copied straight from the widget being edited rather than exposed as inputs — they are owned by
 * the drag/resize canvas, and echoing the baseline is what stops a save from resetting the layout
 * the user just arranged.
 */

import { useEffect, useMemo, useState } from 'react';

import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  FormField,
  FormFieldInput,
  FormFieldSwitch,
  FormFieldTextarea,
} from '@coordio/ui';

import type { DashboardWidgetDto, UpdateDashboardWidgetRequest } from '../../types/dashboard';

import './dashboardWidgets.scss';

export interface WidgetEditorDialogProps {
  /**
   * Whether the dialog is open.
   */
  open: boolean;

  /**
   * The widget being edited.
   */
  widget: DashboardWidgetDto | null;

  /**
   * Called when the dialog should be closed.
   */
  onClose: () => void;

  /**
   * Called when the user saves changes.
   */
  onSave: (widgetId: string, request: UpdateDashboardWidgetRequest) => Promise<void> | void;
}

/**
 * Widget editor dialog component.
 */
export const WidgetEditorDialog = ({ open, widget, onClose, onSave }: WidgetEditorDialogProps) => {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [widgetType, setWidgetType] = useState('');
  const [configurationJson, setConfigurationJson] = useState('{}');
  const [isVisible, setIsVisible] = useState(true);

  const baseline = useMemo(() => widget, [widget]);

  useEffect(() => {
    if (!widget) return;
    setTitle(widget.title ?? '');
    setDescription(widget.description ?? '');
    setWidgetType(widget.widgetType ?? '');
    setConfigurationJson(widget.configurationJson ?? '{}');
    setIsVisible(widget.isVisible ?? true);
  }, [widget]);

  if (!baseline) return null;

  const buildRequest = (): UpdateDashboardWidgetRequest => {
    return {
      widgetType: widgetType.trim(),
      title: title.trim(),
      description: description.trim() || undefined,
      configurationJson: configurationJson?.trim() || '{}',
      positionX: baseline.positionX,
      positionY: baseline.positionY,
      width: baseline.width,
      height: baseline.height,
      isVisible,
      sortOrder: baseline.sortOrder,
    };
  };

  const handleSave = async () => {
    if (!baseline) return;
    await onSave(baseline.id, buildRequest());
    onClose();
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      <DialogContent className="widget-editor" data-testid="widget-editor-dialog">
        <DialogHeader>
          <DialogTitle>Edit widget</DialogTitle>
          <DialogDescription>Rename the widget, toggle its visibility, or edit its raw configuration.</DialogDescription>
        </DialogHeader>

        <div className="widget-editor__fields">
          <FormField label="Widget type" description="Used for rendering in the dashboard." id="widget-type">
            <FormFieldInput value={widgetType} onChange={(e) => setWidgetType(e.target.value)} />
          </FormField>

          <FormField label="Title" id="widget-title">
            <FormFieldInput value={title} onChange={(e) => setTitle(e.target.value)} />
          </FormField>

          <FormField label="Description" id="widget-description">
            <FormFieldTextarea
              rows={2}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </FormField>

          <FormField label="Visible" id="widget-visible">
            <FormFieldSwitch checked={isVisible} onCheckedChange={setIsVisible} />
          </FormField>

          <FormField
            label="Configuration JSON"
            description="Stored as raw JSON. Use it to configure queries, chart options, thresholds, etc."
            id="widget-config"
          >
            <FormFieldTextarea
              rows={8}
              value={configurationJson}
              onChange={(e) => setConfigurationJson(e.target.value)}
            />
          </FormField>
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="button"
            variant="default"
            size="sm"
            onClick={handleSave}
            disabled={!title.trim() || !widgetType.trim()}
          >
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
